import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Grid, MenuItem, Typography } from '@mui/material';

import { AvatarTypes } from '../../../components/designe/app-avatar/AppAvatar';
import { AppBarWithButtons } from '../../../components/designe/app-bar-with-action-buttons/AppBarWithButtons';
import { AppBarWithActionButtons } from '../../../components/designe/app-bar-with-buttons/AppBarWithActionButtons';
import AppEntityAvatar, {
  AvatarSize,
} from '../../../components/designe/app-entity-avatar/AppEntityAvatar';
import AppFormSelectWrapper from '../../../components/designe/app-form-select-wrapper';
import { useGetLanguages } from '../../../services/redux/api/translationsAPI';
import {
  AccessScope,
  FormMode,
  PostsInteractionScope,
} from '../../../shared/constants';
import theme from '../../../styles/theme';
import { IExerciseProperty } from '../../../types/interfaces/exercise-property';
import { ILanguage } from '../../../types/interfaces/language';
import { useStyles } from './UserConfig.styles';
import { useUserConfigComponent } from './useUserConfigComponent';

export default function UserConfig(props) {
  const { data: languages } = useGetLanguages();
  const { t } = useTranslation();
  const { classes } = useStyles(theme);
  const { formik, isLoading, mode, setMode, onCancelChanges } =
    useUserConfigComponent();

  function getItem(item: IExerciseProperty | ILanguage) {
    return (
      <Box display="flex" gap={1} alignItems="center">
        <AppEntityAvatar
          entity={item}
          size={AvatarSize.s}
          type={AvatarTypes.round}
        />
        <Typography variant="text-md">
          {item && ('name' in item ? item.name : 'title' in item && item.title)}
        </Typography>
      </Box>
    );
  }

  function getPagePrivacyOptions() {
    return Object.values(AccessScope).map((scope) => ({
      id: scope,
      name: t(`settings.pagePrivacy_${scope}`),
    }));
  }

  function getPostPrivacyOptions() {
    return Object.values(PostsInteractionScope).map((scope) => ({
      id: scope,
      name: t(`settings.postPrivacy_${scope}`),
    }));
  }

  function getCommentPrivacyOptions() {
    return Object.values(PostsInteractionScope).map((scope) => ({
      id: scope,
      name: t(`settings.commentPrivacy_${scope}`),
    }));
  }

  const pagePrivacyOptions = getPagePrivacyOptions();
  const postPrivacyOptions = getPostPrivacyOptions();
  const commentPrivacyOptions = getCommentPrivacyOptions();

  return (
    <div className={classes.userConfigWrapper}>
      <form onSubmit={formik.handleSubmit} className={classes.userConfigForm}>
        {mode !== FormMode.edit && (
          <Box className={classes.buttonActionWrapper}>
            <AppBarWithActionButtons
              onEditClick={() => setMode(FormMode.edit)}
            />
          </Box>
        )}
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <AppFormSelectWrapper
              label={t('exercises.language')}
              className={classes.selectorWithContent}
              options={languages}
              fieldName="title"
              disableClear
              disableSearch
              viewMode={mode === FormMode.view}
              defaultValue={languages?.find(
                (e) => e.id === formik.values.language?.id,
              )}
              name="language"
              formik={formik}
              itemWrapperCallback={getItem}
            />
          </Grid>
          <Grid item xs={12}>
            <AppFormSelectWrapper
              label={t('settings.pagePrivacy')}
              options={pagePrivacyOptions}
              disableClear
              disableSearch
              viewMode={mode === FormMode.view}
              defaultValue={pagePrivacyOptions?.find(
                (e) => e.id === formik.values.scope,
              )}
              onChange={(e) =>
                formik.setFieldValue('scope', e.target.value?.id)
              }
              name="scope"
              formik={formik}
            />
          </Grid>
          <Grid item xs={12}>
            <AppFormSelectWrapper
              label={t('settings.whoCanAddPost')}
              options={postPrivacyOptions}
              disableClear
              disableSearch
              viewMode={mode === FormMode.view}
              defaultValue={postPrivacyOptions?.find(
                (e) => e.id === formik.values.postsScope,
              )}
              onChange={(e) =>
                formik.setFieldValue('postsScope', e.target.value?.id)
              }
              name="postsScope"
              formik={formik}
            />
          </Grid>
          <Grid item xs={12}>
            <AppFormSelectWrapper
              label={t('settings.whoCanComment')}
              options={commentPrivacyOptions}
              disableClear
              disableSearch
              viewMode={mode === FormMode.view}
              defaultValue={commentPrivacyOptions?.find(
                (e) => e.id === formik.values.commentsScope,
              )}
              onChange={(e) =>
                formik.setFieldValue('commentsScope', e.target.value?.id)
              }
              name="commentsScope"
              formik={formik}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {mode === FormMode.edit && (
            <Box className={classes.buttonWrapper}>
              <AppBarWithButtons
                loading={isLoading}
                disabled={!formik.dirty}
                onCancelClick={onCancelChanges}
              />
            </Box>
          )}
        </Grid>
      </form>
    </div>
  );
}
